import { NavLink, Navigate, Route, Routes } from 'react-router';
import type { SessionUser } from '../api/types.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { AccountsPage } from './pages/AccountsPage.js';
import { AccountDetailPage } from './pages/AccountDetailPage.js';
import { AccountCreatePage } from './pages/AccountCreatePage.js';
import { ConditionsOverviewPage } from './pages/ConditionsOverviewPage.js';
import { QuotesPage } from './pages/QuotesPage.js';
import { QuoteDetailPage } from './pages/QuoteDetailPage.js';
import { QuickQuotePage } from './pages/QuickQuotePage.js';
import { PriceListsPage } from './pages/PriceListsPage.js';
import { OePricingPage } from './pages/OePricingPage.js';
import { ArticlesPage } from './pages/ArticlesPage.js';
import { GlobalOverridesPage } from './pages/GlobalOverridesPage.js';
import { SearchTagsPage } from './pages/SearchTagsPage.js';
import { TemplatesPage } from './pages/TemplatesPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { InternalUsersPage } from './pages/InternalUsersPage.js';

/**
 * Adminbereich, M7.
 *
 * Die Navigation zeigt nur, wofuer die Rolle Rechte hat. Das ist Bedienkomfort,
 * kein Schutz -- der Schutz steht am Server. Wer eine ausgeblendete Route direkt
 * aufruft, bekommt vom Server 403 und von der Seite eine klare Meldung.
 */
interface NavItem {
  to: string;
  label: string;
  roles: readonly SessionUser['role'][];
}

const NAV: readonly NavItem[] = [
  { to: '/admin', label: 'Dashboard', roles: ['sales', 'operator_admin'] },
  { to: '/admin/kunden', label: 'Kunden', roles: ['sales', 'operator_admin'] },
  { to: '/admin/konditionen', label: 'Konditionen', roles: ['sales', 'operator_admin'] },
  {
    to: '/admin/globale-regeln',
    label: 'Globale Rabattregeln',
    roles: ['operator_admin'],
  },
  { to: '/admin/angebote', label: 'Angebote', roles: ['sales', 'operator_admin'] },
  {
    to: '/admin/schnellangebot',
    label: 'Schnellangebot',
    roles: ['sales', 'operator_admin'],
  },
  { to: '/admin/preislisten', label: 'Preislisten', roles: ['operator_admin'] },
  { to: '/admin/oe-preise', label: 'G-ST-P/OE Preise', roles: ['operator_admin'] },
  { to: '/admin/artikel', label: 'Artikel', roles: ['operator_admin'] },
  { to: '/admin/suchbegriffe', label: 'Suchbegriffe', roles: ['operator_admin'] },
  { to: '/admin/vorlagen', label: 'Vorlagen', roles: ['operator_admin'] },
  { to: '/admin/benutzer', label: 'Interne Benutzer', roles: ['operator_admin'] },
  { to: '/admin/einstellungen', label: 'Einstellungen', roles: ['operator_admin'] },
];

export function AdminShell({ user }: { user: SessionUser }): React.JSX.Element {
  const admin = user.role === 'operator_admin';
  const sichtbar = NAV.filter((n) => n.roles.includes(user.role));

  return (
    <div className="admin-layout">
      <nav className="admin-nav" aria-label="Administration">
        {sichtbar.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === '/admin'}
            className={({ isActive }) =>
              isActive ? 'admin-nav-link selected' : 'admin-nav-link'
            }
          >
            {n.label}
          </NavLink>
        ))}
      </nav>
      <main className="admin-main">
        <Routes>
          <Route index element={<DashboardPage />} />
          <Route path="kunden" element={<AccountsPage />} />
          <Route path="kunden/neu" element={<AccountCreatePage user={user} />} />
          <Route path="kunden/:id/*" element={<AccountDetailPage user={user} />} />
          <Route path="konditionen" element={<ConditionsOverviewPage />} />
          {admin && <Route path="globale-regeln" element={<GlobalOverridesPage />} />}
          <Route path="angebote" element={<QuotesPage />} />
          <Route path="angebote/:id" element={<QuoteDetailPage />} />
          <Route path="schnellangebot" element={<QuickQuotePage />} />
          {admin && <Route path="preislisten" element={<PriceListsPage />} />}
          {admin && <Route path="oe-preise" element={<OePricingPage />} />}
          {admin && <Route path="artikel" element={<ArticlesPage />} />}
          {admin && <Route path="suchbegriffe" element={<SearchTagsPage />} />}
          {admin && <Route path="vorlagen" element={<TemplatesPage />} />}
          {admin && <Route path="benutzer" element={<InternalUsersPage user={user} />} />}
          {admin && <Route path="einstellungen" element={<SettingsPage />} />}
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </main>
    </div>
  );
}
